import React from 'react';
import { useFxRate } from '../context/FxRateContext';
import { formatUSD } from '../utils/money';
import { safeHtml } from '../utils/safeHtml';

/**
 * Augment an HTML string by appending USD equivalents next to every PHP price
 * (e.g. "₱4,500" or "PHP 4,500"), then run the whole thing through DOMPurify.
 * The returned string is safe to pass into `dangerouslySetInnerHTML`.
 */
export function injectUsdIntoHtml(html, phpUsdRate) {
  if (!html || typeof html !== 'string') return '';

  // Step 1 — append USD next to peso amounts (skip if no FX rate yet).
  let out = html;
  if (phpUsdRate && phpUsdRate > 0) {
    const re = /(₱|PHP\s*)([\d][\d,]*(?:\.\d+)?)(?!\s*\()/gi;
    out = out.replace(re, (match, prefix, numStr) => {
      const num = parseFloat(numStr.replace(/,/g, ''));
      if (!Number.isFinite(num) || num <= 0) return match;
      const usd = formatUSD(num * phpUsdRate);
      return `${match} <span class="text-[#5C7599] font-normal opacity-80">(${usd})</span>`;
    });
  }

  // Step 2 — sanitize before it hits the DOM. Strips inline scripts,
  // event handlers, javascript: URIs, etc. while keeping rich-text markup.
  return safeHtml(out);
}

/**
 * SmartPrice — given a free-form price string like "₱4,500" or "P 4500/mo"
 * or "$500", render the same string PLUS the converted-currency in parens.
 *
 * Useful for CMS-managed content where prices are stored as arbitrary strings
 * that admins type in directly.
 *
 *   <SmartPrice text={item.price} />
 *     ₱4,500   →  "₱4,500 ($72.96)"
 *     P4,500/mo→  "P4,500/mo ($72.96)"
 *     $500     →  "$500 (₱30,837)"
 *     Free     →  "Free"      (unchanged when no number found)
 */
export function SmartPrice({ text, usdClassName = '' }) {
  const { phpUsdRate, usdPhpRate } = useFxRate();
  if (!text || typeof text !== 'string') return null;

  // Match the FIRST currency-prefixed number in the string.
  // Only match ₱ or PHP (strict — bare 'P' is too ambiguous).
  const phpMatch = text.match(/(?:₱|PHP\s*)\s*([\d.,]+)/i);
  const usdMatch = text.match(/\$\s*([\d.,]+)/);

  const renderExtra = (extraStr) => (
    <span className={usdClassName || undefined}> ({extraStr})</span>
  );

  if (phpMatch && phpUsdRate > 0) {
    const num = parseFloat(phpMatch[1].replace(/,/g, ''));
    if (Number.isFinite(num) && num > 0) {
      return <>{text}{renderExtra(formatUSD(num * phpUsdRate))}</>;
    }
  }

  if (usdMatch && (usdPhpRate > 0 || phpUsdRate > 0)) {
    const num = parseFloat(usdMatch[1].replace(/,/g, ''));
    if (Number.isFinite(num) && num > 0) {
      const phpAmt = usdPhpRate > 0 ? num * usdPhpRate : num / phpUsdRate;
      return <>{text}{renderExtra(`₱${Math.round(phpAmt).toLocaleString('en-PH')}`)}</>;
    }
  }

  return <>{text}</>;
}

export default SmartPrice;
